import { FC, MouseEventHandler } from 'react';
import { IoLink, IoLinkSharp, IoUnlink } from 'react-icons/io5';

import { Button, Tooltip } from '@mantine/core';

import { useEditConnectionContext } from '@diagram/components/diagram-sidebar/sidebars/edit-connection/context';
import { useProductLinking } from '@diagram/hooks/use-product-linking';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { ConnectionService } from '@diagram/services/ConnectionService';
import { DiagramSyncService } from '@diagram/services/DiagramSyncService';
import { ProductLinkingService } from '@diagram/services/ProductLinkingService';
import { SidebarService } from '@diagram/services/SidebarService';
import { SidebarTab } from '@diagram/state/sidebar';

import { all, Cable, Component, DiagramComponentInstance, FeatureKey } from 'models';

import { SubscriptionOnboardingMessage } from 'components/subscriptions/SubscriptionOnboardingMessage';
import { useFeatureAccess } from 'hooks/use-feature-access';
import { LocalNotificationService } from 'services/LocalNotificationService';

export const ComponentDiagramButton: FC<{ component: Component }> = ({ component }) => {
    const canLinkProduct = useFeatureAccess(FeatureKey.LINK_PRODUCT);

    if (!canLinkProduct) {
        return (
            <SubscriptionOnboardingMessage>
                Want to add link products from the product catalog?
            </SubscriptionOnboardingMessage>
        );
    }

    if (component.type === 'cable') {
        return <ComponentDiagramButtonConnection component={component} />;
    }

    return <ComponentDiagramButtonComponentInstance component={component} />;
};

const ComponentDiagramButtonComponentInstance = ({ component }: { component: Component }) => {
    const { componentInstance, linkedComponentId } = useProductLinking();

    if (!componentInstance) return null;

    if (component.type !== componentInstance.componentType) {
        return <TypeMismatchLinkButton componentInstance={componentInstance} />;
    }

    const addToDesign: MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.stopPropagation();

        try {
            ComponentInstanceService.linkProduct({
                componentInstance,
                productCatalogComponent: component,
            });

            DiagramSyncService.save();
            ProductLinkingService.updateLinkedComponentId(component.id);
        } catch (error: any) {
            LocalNotificationService.showError({ message: error.message ?? 'Error linking product' });
        }
    };

    const linkedToComponent = linkedComponentId === component.id;

    return (
        <Button
            leftSection={<IoLinkSharp />}
            onClick={addToDesign}
            disabled={linkedToComponent}
            size="compact-xs"
            variant="outline"
            style={{ alignSelf: 'flex-start' }}
        >
            {linkedToComponent ? 'Linked' : 'Link'} to {componentInstance.designator}
        </Button>
    );
};

const TypeMismatchLinkButton = ({ componentInstance }: { componentInstance: DiagramComponentInstance }) => (
    <Tooltip
        label={`${componentInstance.designator} can only be linked to ${all[componentInstance.componentType].plural}`}
        withArrow
    >
        <Button
            leftSection={<IoLinkSharp />}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            data-disabled
            size="compact-xs"
            variant="outline"
            style={{ alignSelf: 'flex-start' }}
        >
            Link to {componentInstance.designator}
        </Button>
    </Tooltip>
);

const ComponentDiagramButtonConnection = ({ component }: { component: Component }) => {
    const { connection } = useEditConnectionContext();

    const linkCable = async (cable: Cable) => {
        try {
            ConnectionService.linkCable({ connection, cable });

            DiagramSyncService.save();
            SidebarService.setActiveTab(SidebarTab.SPECIFICATIONS);
        } catch (error: any) {
            LocalNotificationService.showError({ message: error.message ?? 'Error linking cable' });
        }
    };

    if (!connection) {
        return null;
    }

    return (
        <>
            {connection.cableId === component.id ? (
                <Button
                    size="compact-xs"
                    variant="outline"
                    onClick={() => {
                        ConnectionService.unlinkCable(connection.id);
                        DiagramSyncService.save();
                    }}
                    leftSection={<IoUnlink />}
                >
                    Unlink this cable
                </Button>
            ) : (
                <Button
                    size="compact-xs"
                    variant="outline"
                    onClick={() => linkCable(component as Cable)}
                    leftSection={<IoLink />}
                >
                    Link this cable
                </Button>
            )}
        </>
    );
};
